// Travel insurance journey.
//
// Every screen in the funnel — its route, its stepper label, its left-rail
// copy, and every field it asks for — is described here. Adding a question is
// one object in a `fields` array; removing one is deleting that object.

/** @type {import("../../form/types").JourneyConfig} */
const travel = {
  key: "travel",
  label: "Travel Insurance",

  // The exact value redux stores in `insuranceType`; the route guard
  // restores it from here after a refresh or a deep link.
  insuranceType: "Travel Insurance",
  entry: "/travel-landing",
  landing: "landing:travel",
  caseIdPrefix: "T",

  // Journey-level rail defaults. Each step may override any of these.
  rail: {
    greeting: "Hi there",
    image: "/assets/images/aeroplane-icon.png",
    bullets: [
      "Instant policies online",
      "Flexible payment options",
      "Insurers you can trust",
      "24/7 claims support",
    ],
  },

  steps: [
    {
      key: "where-to",
      summary: ["planning"],
      label: "Where to",
      path: "/travel-planning",
      mobileTitle: "Where to?",
      rail: { title: "where are you", accent: "travelling?" },
      // Picking a direction card is the navigation — no forward CTA.
      hideCta: true,
      // The chosen direction also decides which upstream product line the
      // quote calls (/premium/inbound vs /premium/outbound), and those screens
      // read it from redux — so mirror it there as well as persisting it.
      dispatchOnSave: { planning: "SET_TRAVEL_PLANNING_TYPE" },
      form: {
        id: "travel-direction",
        sections: [
          {
            fields: [
              {
                name: "planning",
                type: "choice",
                label: "Where are you planning to travel?",
                required: true,
                col: 12,
                props: { illustrated: true },
                options: [
                  {
                    value: "outbound",
                    label: "Travelling from UAE",
                    subtitle: "Outbound trip",
                    image: "/assets/images/traveling-from-uae.png",
                    badge: "Most popular",
                    hint: "You live in the UAE and are heading abroad",
                    perks: [
                      "Worldwide emergency medical cover",
                      "Schengen visa cover (EUR 30,000+)",
                      "Flight delay & baggage cover",
                    ],
                  },
                  {
                    value: "inbound",
                    label: "Travelling to UAE",
                    subtitle: "Inbound trip",
                    image: "/assets/images/traveling-to-uae.png",
                    badge: "Visa-friendly",
                    hint: "You are visiting the UAE from another country",
                    perks: [
                      "Meets UAE visit-visa requirements",
                      "Inpatient hospitalisation cover",
                      "Cashless network across UAE hospitals",
                    ],
                  },
                ],
              },
            ],
          },
        ],
      },
    },

    {
      key: "your-trip",
      summary: ["coverage", "destination"],
      label: "Your trip",
      path: "/travel-details",
      mobileTitle: "Your trip",
      rail: {
        title: "tell us about your",
        accent: "trip",
        subtitle: "Cover, destination and dates decide which plans you can buy.",
      },
      cta: "Continue to documents",
      form: {
        id: "travel-trip",
        sections: [
          {
            fields: [
              {
                name: "coverage",
                type: "choice",
                label: "What cover do you need?",
                required: true,
                col: 12,
                options: { source: "reference", domain: "travel-coverage" },
              },
            ],
          },
          {
            title: "Destination",
            // Inbound trips are, by definition, to the UAE — asking where is
            // noise, so the whole section disappears.
            visibleWhen: { field: "planning", notEquals: "inbound" },
            fields: [
              {
                name: "destination_type",
                type: "radio",
                label: "How would you like to tell us where you're going?",
                required: true,
                col: 12,
                defaultValue: "region",
                options: [
                  { value: "region", label: "Select a region" },
                  { value: "airport", label: "Enter an IATA code" },
                ],
              },
              {
                name: "destination",
                type: "select",
                label: "Region",
                col: 12,
                requiredWhen: { field: "destination_type", equals: "region" },
                visibleWhen: { field: "destination_type", equals: "region" },
                options: { source: "reference", domain: "travel-region" },
              },
              {
                name: "iata_code",
                type: "text",
                label: "IATA airport code",
                placeholder: "DXB",
                help: "The three-letter code of your destination airport",
                col: 6,
                requiredWhen: { field: "destination_type", equals: "airport" },
                visibleWhen: { field: "destination_type", equals: "airport" },
                rules: [
                  {
                    rule: "regex",
                    pattern: "^[A-Za-z]{3}$",
                    message: "An IATA code is exactly three letters, e.g. DXB",
                  },
                ],
                props: { maxLength: 3, transform: "uppercase" },
              },
            ],
          },
          {
            title: "Dates",
            fields: [
              {
                name: "start_date",
                type: "date",
                label: "Travel start date",
                required: true,
                rules: ["futureDate"],
                props: { minDate: "today" },
              },
              {
                name: "end_date",
                type: "date",
                label: "Travel end date",
                required: true,
                rules: [{ rule: "afterField", field: "start_date", message: "Your return date must be on or after your departure date" }],
                props: { minDate: "today" },
              },
            ],
          },
        ],
      },
    },

    {
      key: "your-documents",
      label: "Your documents",
      path: "/document-upload",
      screen: "document-upload",
      mobileTitle: "Your documents",
      rail: {
        title: "upload your",
        accent: "documents",
        subtitle:
          "We read your Emirates ID automatically so you don't have to type it in.",
      },
      cta: "Continue to your details",
    },

    {
      key: "your-details",
      summary: ["first_name", "nationality"],
      label: "Your details",
      path: "/traveller-details",
      mobileTitle: "Your details",
      rail: {
        title: "tell us about",
        accent: "yourself",
        subtitle: "We've pre-filled what we could read from your passport.",
      },
      cta: "See your quotes",
      form: {
        id: "traveller-details",
        sections: [
          {
            fields: [
              {
                name: "title",
                type: "select",
                label: "Title",
                col: 3,
                required: true,
                options: { source: "reference", domain: "title" },
              },
              {
                name: "first_name",
                type: "text",
                label: "First name",
                help: "Exactly as printed in your passport",
                col: 4,
                required: true,
                ocrKeys: "Name",
                ocrTransform: "firstName",
              },
              {
                name: "middle_name",
                type: "text",
                label: "Middle name",
                help: "Leave blank if your passport shows none",
                col: 5,
                ocrKeys: "Name",
                ocrTransform: "middleName",
              },
              {
                name: "last_name",
                type: "text",
                label: "Last name",
                help: "Your family name, as in your passport",
                required: true,
                ocrKeys: "Name",
                ocrTransform: "lastName",
              },
              {
                name: "dob",
                type: "date",
                label: "Date of birth",
                required: true,
                rules: [{ rule: "age", min: 0, max: 120 }],
                ocrKeys: "Date_of_Birth",
                props: { maxDate: "today" },
              },
              {
                name: "gender",
                type: "select",
                label: "Gender",
                required: true,
                options: { source: "reference", domain: "gender" },
                ocrKeys: "Sex",
              },
              {
                name: "nationality",
                type: "select",
                label: "Nationality",
                required: true,
                options: { source: "reference", domain: "nationality" },
                ocrKeys: "Nationality",
              },
              {
                name: "email",
                type: "email",
                label: "Email",
                help: "Your policy certificate is emailed here",
                required: true,
                rules: ["email"],
                placeholder: "name@example.com",
              },
              {
                name: "mobile",
                type: "phone",
                label: "Mobile number",
                required: true,
                rules: ["uaeMobile"],
              },
              {
                name: "is_uae_resident",
                type: "checkbox",
                label: "I am a UAE resident",
                col: 12,
              },
            ],
          },
          {
            title: "Passport",
            fields: [
              {
                name: "passport_number",
                type: "text",
                label: "Passport number",
                help: "6–12 letters and digits, from the photo page",
                required: true,
                rules: ["passport"],
              },
              {
                name: "passport_expiry_date",
                type: "date",
                label: "Passport expiry",
                required: true,
                rules: ["notExpired"],
                props: { minDate: "today" },
              },
              {
                name: "emirates_id",
                type: "text",
                label: "Emirates ID",
                help: "15 digits from the front of the card",
                placeholder: "784-YYYY-XXXXXXX-X",
                rules: ["emiratesId"],
                visibleWhen: { field: "is_uae_resident", truthy: true },
                ocrKeys: "ID_Number",
              },
            ],
          },
          {
            title: "Address",
            fields: [
              { name: "address_1", type: "text", label: "Address line 1", col: 12, required: true, help: "Building name or number, and street" },
              { name: "address_2", type: "text", label: "Address line 2", col: 12, help: "Area or district" },
              { name: "address_3", type: "text", label: "City", help: "City you live in" },
              { name: "address_4", type: "text", label: "Country", help: "Country of residence, not your destination" },
            ],
          },
        ],
      },
    },

    {
      key: "choose-plan",
      label: "Choose plan",
      path: "/quote-details",
      screen: "quote-details",
      screenProps: { product: "travel" },
      aliasPaths: [{ path: "/quote-compare", screen: "quote-compare" }],
      layout: "wide",
      mobileTitle: "Choose your plan",
      rail: { title: "choose your", accent: "plan" },
      hideCta: true,
    },

    {
      key: "review-pay",
      label: "Review & pay",
      path: "/plan-summary",
      screen: "plan-summary",
      screenProps: { product: "travel" },
      layout: "wide",
      mobileTitle: "Review & pay",
      rail: { title: "one last", accent: "step" },
      cta: "Confirm & purchase",
    },
  ],
};

export default travel;
